import {
  themes,
  llamayaPalette,
  greyMultibrandPalette,
  masmovilPalette,
  CustomTheme,
} from '@masfront/common-b2c'

import { makeStyles, createStyles } from '@material-ui/core'

import { Brand, getBrandFromThemeName } from '~/brand'
import { SubscriptionStatusGroup, SubscriptionStatus } from '~/customer/model/customer.model'
import { deepMerge } from '~/utils'

interface ProductStatusDetailProps {
  statusGroup: SubscriptionStatusGroup
  status?: SubscriptionStatus
}

const commonStyles = {
  root: {},
}

const styles = {
  [Brand.masmovil]: deepMerge(commonStyles, {
    firstAlert: {
      backgroundColor: ({ statusGroup }: ProductStatusDetailProps) => {
        switch (statusGroup) {
          case SubscriptionStatusGroup.ACTIVE:
            return themes.masmovil.palette.success.light
          case SubscriptionStatusGroup.ON_FLY:
            return themes.masmovil.palette.warning.main
          case SubscriptionStatusGroup.LOCKED:
          case SubscriptionStatusGroup.TERMINATED:
            return themes.masmovil.palette.error.main
          case SubscriptionStatusGroup.PENDING:
          case SubscriptionStatusGroup.NO_GROUP:
          default:
            return greyMultibrandPalette[300]
        }
      },
      '& .MuiAlert-message': {
        color: ({ statusGroup }: ProductStatusDetailProps) => {
          switch (statusGroup) {
            case SubscriptionStatusGroup.ACTIVE:
            case SubscriptionStatusGroup.ON_FLY:
            case SubscriptionStatusGroup.PENDING:
            case SubscriptionStatusGroup.NO_GROUP:
              return greyMultibrandPalette[900]
            default:
              return themes.masmovil.palette.common.white
          }
        },
      },
    },
    icon: {
      '& .MuiSvgIcon-root': {
        color: ({ statusGroup }: ProductStatusDetailProps) => {
          switch (statusGroup) {
            case SubscriptionStatusGroup.ACTIVE:
            case SubscriptionStatusGroup.ON_FLY:
            case SubscriptionStatusGroup.PENDING:
            case SubscriptionStatusGroup.NO_GROUP:
              return greyMultibrandPalette[900]
            default:
              return themes.masmovil.palette.common.white
          }
        },
      },
    },
    secondAlert: {
      backgroundColor: ({ status }: ProductStatusDetailProps) => {
        switch (status) {
          case SubscriptionStatus.ACTIVATED:
            return masmovilPalette.green[200]
          case SubscriptionStatus.INSTALLED:
          case SubscriptionStatus.PREACTIVATED:
            return masmovilPalette.orange[200]
          case SubscriptionStatus.LOCKED_DUE_BALANCE:
          case SubscriptionStatus.TEMPORARY_LOCKED:
          case SubscriptionStatus.LOCKED_DUE_CONSUMPTION_LIMIT:
          case SubscriptionStatus.TERMINATED:
            return masmovilPalette.red[200]
          case SubscriptionStatus.TERMINATION_PENDING:
          case SubscriptionStatus.SUSPENSION_PENDING:
          case SubscriptionStatus.RECONNECTION_PENDING:
          case SubscriptionStatus.TEMPORARY_LOCK_PENDING:
          default:
            return greyMultibrandPalette[150]
        }
      },
    },
    triangle: {
      borderLeft: '10px solid',
      borderLeftColor: ({ statusGroup }: ProductStatusDetailProps) => {
        switch (statusGroup) {
          case SubscriptionStatusGroup.ACTIVE:
            return themes.masmovil.palette.success.light
          case SubscriptionStatusGroup.ON_FLY:
            return themes.masmovil.palette.warning.main
          case SubscriptionStatusGroup.LOCKED:
          case SubscriptionStatusGroup.TERMINATED:
            return themes.masmovil.palette.error.main
          case SubscriptionStatusGroup.PENDING:
          case SubscriptionStatusGroup.NO_GROUP:
          default:
            return greyMultibrandPalette[300]
        }
      },
    },
  }),
  [Brand.llamaya]: deepMerge(commonStyles, {
    firstAlert: {
      backgroundColor: ({ statusGroup }: ProductStatusDetailProps) => {
        switch (statusGroup) {
          case SubscriptionStatusGroup.ACTIVE:
            return themes.llamaya.palette.success.light
          case SubscriptionStatusGroup.ON_FLY:
            return themes.llamaya.palette.warning.main
          case SubscriptionStatusGroup.LOCKED:
          case SubscriptionStatusGroup.TERMINATED:
            return themes.llamaya.palette.error.main
          case SubscriptionStatusGroup.PENDING:
          case SubscriptionStatusGroup.NO_GROUP:
          default:
            return greyMultibrandPalette[300]
        }
      },
      '& .MuiAlert-message': {
        color: ({ statusGroup }: ProductStatusDetailProps) => {
          switch (statusGroup) {
            case SubscriptionStatusGroup.ACTIVE:
            case SubscriptionStatusGroup.ON_FLY:
            case SubscriptionStatusGroup.PENDING:
            case SubscriptionStatusGroup.NO_GROUP:
              return themes.llamaya.palette.text.secondary
            default:
              return themes.llamaya.palette.common.white
          }
        },
      },
    },
    icon: {
      '& .MuiSvgIcon-root': {
        color: ({ statusGroup }: ProductStatusDetailProps) => {
          switch (statusGroup) {
            case SubscriptionStatusGroup.ACTIVE:
            case SubscriptionStatusGroup.ON_FLY:
            case SubscriptionStatusGroup.PENDING:
            case SubscriptionStatusGroup.NO_GROUP:
              return themes.llamaya.palette.text.secondary
            default:
              return themes.llamaya.palette.common.white
          }
        },
      },
    },
    secondAlert: {
      backgroundColor: ({ status }: ProductStatusDetailProps) => {
        switch (status) {
          case SubscriptionStatus.ACTIVATED:
            return llamayaPalette.green[200]
          case SubscriptionStatus.INSTALLED:
          case SubscriptionStatus.PREACTIVATED:
            return llamayaPalette.orange[200]
          case SubscriptionStatus.LOCKED_DUE_BALANCE:
          case SubscriptionStatus.TEMPORARY_LOCKED:
          case SubscriptionStatus.LOCKED_DUE_CONSUMPTION_LIMIT:
          case SubscriptionStatus.TERMINATED:
            return llamayaPalette.red[200]
          case SubscriptionStatus.TERMINATION_PENDING:
          case SubscriptionStatus.SUSPENSION_PENDING:
          case SubscriptionStatus.RECONNECTION_PENDING:
          case SubscriptionStatus.TEMPORARY_LOCK_PENDING:
          default:
            return greyMultibrandPalette[150]
        }
      },
    },
    triangle: {
      borderLeft: '10px solid',
      borderLeftColor: ({ statusGroup }: ProductStatusDetailProps) => {
        switch (statusGroup) {
          case SubscriptionStatusGroup.ACTIVE:
            return themes.llamaya.palette.success.light
          case SubscriptionStatusGroup.ON_FLY:
            return themes.llamaya.palette.warning.main
          case SubscriptionStatusGroup.LOCKED:
          case SubscriptionStatusGroup.TERMINATED:
            return themes.llamaya.palette.error.main
          case SubscriptionStatusGroup.PENDING:
          case SubscriptionStatusGroup.NO_GROUP:
          default:
            return greyMultibrandPalette[300]
        }
      },
    },
  }),
}

const useBrandStyles = makeStyles((theme: CustomTheme) => {
  const stylesRules = styles[getBrandFromThemeName(theme.name)]

  return createStyles(stylesRules)
})

export default useBrandStyles
